import { MedicationEntryTabs } from "@/components/medication/MedicationEntryTabs";
import { ShareScheduleButton } from "../components/schedule/ShareScheduleButton";
import { useState, useEffect, lazy, Suspense, useRef } from "react";
import type { Medication } from "@/types/medication";
import { Helmet } from "react-helmet-async";
import { useIsMobile } from "@/hooks/use-mobile";
import { motion, AnimatePresence } from "framer-motion";
import { useSkeletonLoading } from "@/hooks/use-skeleton-loading";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { LandingPageSkeleton } from "@/components/ui/skeleton-loaders/LandingPageSkeleton";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { MedicationSchedule } from "@/components/MedicationSchedule";
import { FirstRunExperience } from "@/components/fre/FirstRunExperience";

// Lazy load non-critical components per requirements
// "Anything below the MedicationSchedule component is not critical and primarily for SEO purposes"
const Features = lazy(() => import("@/components/landing/Features"));
const Caregivers = lazy(() => import("@/components/landing/Caregivers"));
const HowItWorks = lazy(() => import("@/components/landing/HowItWorks"));
const FAQSection = lazy(() => import("@/components/landing/FAQSection"));
const CtaSection = lazy(() => import("@/components/landing/CtaSection"));

// Application constants 
import { USER_EXPERIENCE_STATES, UserExperienceState } from "@/constants/appConstants";

// Store imports
import { useMedicationStore } from "@/store/medicationStore";

// Lazy load CycleTestUtility - as it's not critical for initial render
const CycleTestUtility = lazy(() => import('../components/CycleTestUtility'));

// Progressive loading strategy with performance monitoring
// No console logs to ensure consistent execution
const HeroLite = lazy(() => {
  const startTime = performance.now();
  return import('../components/ui/HeroMockupLite').then(module => {
    // Performance measurement tracked silently
    return module;
  });
});

// Full version loads after critical content (for desktop, deferred)
const loadFullHeroAfterCriticalContent = () => {
  const startTime = performance.now();
  // Only preload, don't render yet
  return import('../components/ui/HeroMockup').then(module => {
    // Performance measurement tracked silently
    return module;
  });
};

// Preload but don't render initially, will swap later
const HeroFull = lazy(() => {
  return loadFullHeroAfterCriticalContent();
});

// Record initial render start time for performance tracking
const indexRenderStart = performance.now();

// AddMedicationDialog for user medication entry
// Fixed import path to match actual file location
import { AddMedicationDialog } from "@/components/common/AddMedicationDialog";

// Import performance service for metric collection
import { performanceService } from "@/services/PerformanceService";
import { usePerformanceMetrics } from "@/hooks/usePerformanceMetrics";

/**
 * Landing page and main application experience
 * 
 * This component follows React's Rules of Hooks:
 * - All hooks are called unconditionally at the top level
 * - No early returns with conditional rendering
 * - Component maintains consistent execution paths
 */
export default function Index() {
  // Use the enhanced performance metrics hook for comprehensive tracking
  const { 
    measureRender, 
    measureOperation, 
    getCurrentMetrics, 
    markInteractive 
  } = usePerformanceMetrics('Index'); // Hook only accepts component name parameter
  
  // Start measuring the main render cycle
  const endRenderMeasurement = measureRender();
  
  // Hook to detect mobile devices for responsive design
  const isMobile = window.innerWidth < 768;
  
  const [isAddMedicationOpen, setIsAddMedicationOpen] = useState(false);
  
  // Create a ref to track performance metrics and component state
  const perfMetrics = useRef({
    componentRenderStart: indexRenderStart,
    firstPaintTime: 0,
    domCompleteTime: 0,
    initialLoadTime: 0,
    heroLoadTime: 0,
    criticalElementsLoaded: false,
    showFullHero: false
  });
  
  // State to manage progressive loading of components
  const [showFullHero, setShowFullHero] = useState(true);
  
  // Initialize skeleton loading with mobile-optimized minimum display time
  const { isLoading, startLoading, endLoading } = useSkeletonLoading({
    minDisplayTime: isMobile ? 150 : 300 // Even shorter for mobile devices
  });
  
  // Create a ref to track first render for the loading effect
  const isFirstRender = useRef(true);
  
  // Check if user is in Second Run Experience (SRE) by checking if they have medications
  const { getMedicationsForCurrentProfile } = useMedicationStore();
  const [userExperience, setUserExperience] = useState<UserExperienceState>(USER_EXPERIENCE_STATES.FIRST_RUN);
  
  // Determine if user has medications and set the appropriate experience mode
  // This is critical for conditionally rendering the correct UI elements
  useEffect(() => {
    // Start measuring this operation
    const endMeasurement = measureOperation('userExperienceDetection');
    
    try {
      // Retrieve user medications
      const medications = getMedicationsForCurrentProfile();
      const medicationCount = Object.values(medications || {}).flat().length;
      
      // Set user experience state based on medication count
      if (medicationCount > 0) {
        setUserExperience(USER_EXPERIENCE_STATES.SECOND_RUN);
      } else {
        // When user has no medications, show the First Run Experience
        setUserExperience(USER_EXPERIENCE_STATES.FIRST_RUN);
      }
      
      // Record medication count as a metric
      performanceService.recordMetric('Index', 'medicationCount', medicationCount);
    } catch (error) {
      // Fallback to first run experience in case of errors
      setUserExperience(USER_EXPERIENCE_STATES.FIRST_RUN);
      // Record error metric
      performanceService.recordMetric('Index', 'medicationLoadError', 1);
    }
    
    // End measurement and record time
    endMeasurement();
  }, [getMedicationsForCurrentProfile, measureOperation]);
  
  const toggleAddMedicationDialog = () => {
    setIsAddMedicationOpen(!isAddMedicationOpen);
  };
  
  // Effect for initial page load and skeleton management
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      
      try {
        // Mark the start of critical content loading
        perfMetrics.current.firstPaintTime = performance.now() - perfMetrics.current.componentRenderStart;
        
        // Start with loading animation to ensure smooth transition
        startLoading();
        
        // Simulate delay for skeleton loader to handle loading state
        setTimeout(() => {
          const beforeEndLoading = performance.now();
          endLoading();
          
          // Measure how long the endLoading operation took
          perfMetrics.current.initialLoadTime = performance.now() - beforeEndLoading;
        }, 50); // Reduced from 300ms to 50ms
      } catch (error) {
        endLoading(); // Ensure loading ends even if there's an error
      }
      
      // Implement progressive loading for non-critical elements with timing
      const loadNonCritical = () => {
        // Mark critical elements as loaded
        perfMetrics.current.criticalElementsLoaded = true;
        
        // Removed delayed hero component swap - now showing HeroFull immediately
        // setShowFullHero is initialized to true above
      };
      
      // Request idle callback for non-critical operations
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => {
          loadNonCritical();
        });
      } else {
        setTimeout(() => {
          loadNonCritical();
        }, 200);
      }
    }
  }, [measureOperation, startLoading, endLoading]);

  // SEO metadata
  const pageTitle = "Meduler | Medication Management App for Patients & Caregivers";
  const pageDescription = "Meduler helps patients and caregivers manage medications with smart reminders, tracking, and caregiver alerts. Free medication management tools to improve adherence.";
  const canonicalUrl = "https://www.meduler.com";

  // Enhanced performance measurement - called unconditionally to maintain hook execution order
  useEffect(() => {
    // Get comprehensive metrics from our hook
    const metrics = getCurrentMetrics();
    
    // Report metrics via service instead of console logs
    if (performanceService && typeof performanceService.recordMetric === 'function') {
      // Record all relevant metrics
      Object.entries(metrics).forEach(([key, value]) => {
        performanceService.recordMetric('Index', key, value);
      });
      
      // Additional custom metrics
      // Convert enum to string to record as a label - using a different method name for string metrics
      if (typeof performanceService.recordLabelMetric === 'function') {
        performanceService.recordLabelMetric('Index', 'viewModeLabel', String(userExperience));
      }
      // Store numeric representation for quantitative analysis
      performanceService.recordMetric('Index', 'viewMode', userExperience === USER_EXPERIENCE_STATES.FIRST_RUN ? 0 : 1);
      performanceService.recordMetric('Index', 'isMobile', isMobile ? 1 : 0);
    }
    
    // Mark component as interactive which tracks time from mount to interactive
    markInteractive();
    
    // End the full render measurement
    endRenderMeasurement();
  }, [endRenderMeasurement, getCurrentMetrics, markInteractive, userExperience, isMobile]);

  // Using conditional rendering pattern instead of early returns
  // This ensures hooks are called in the same order on every render
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-purple-50 flex flex-col">
      {isLoading ? (
        // Loading state with improved accessibility
        <>
          <Helmet>
            <title>Meduler - Loading Your Medication Management Experience</title>
            <meta name="description" content="Loading your personalized medication management system..." />
          </Helmet>
          <div aria-live="polite" aria-busy="true" role="status">
            <LandingPageSkeleton />
          </div>
        </>
      ) : (
        <>
          {/* SEO Optimization */}
          <Helmet>
            <title>{pageTitle}</title>
            <meta name="description" content={pageDescription} />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <link rel="canonical" href={canonicalUrl} />
            
            {/* OpenGraph / Social Media Meta Tags */}
            <meta property="og:type" content="website" />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={pageDescription} />
            <meta property="og:image" content="https://www.meduler.com/social/og-image.png" />
            <meta property="og:url" content={canonicalUrl} />
            
            {/* Twitter Card Meta Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={pageDescription} />
            <meta name="twitter:image" content="https://www.meduler.com/social/og-image.png" />
            
          </Helmet>

          <main className="flex-grow pb-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
              {/* First Run Experience - Only shown for first-time users */}
              {userExperience === USER_EXPERIENCE_STATES.FIRST_RUN && (
                <section
                  className="py-8 mb-8"
                  data-test-id="fre-hero-section"
                >
                  <FirstRunExperience onAddMedication={toggleAddMedicationDialog} />
                </section>
              )}
              
              {/* Featured MedicationEntryTabs with customizable views - Not shown during first run */}
              {userExperience !== USER_EXPERIENCE_STATES.FIRST_RUN && (
                <section 
                  aria-labelledby="medication-schedule-heading"
                  className={`mt-${isMobile ? 4 : 8} mb-${isMobile ? 8 : 12}`}
                  data-test-id="medication-schedule"
                  data-view-mode={userExperience}
                >
                  {userExperience === USER_EXPERIENCE_STATES.SECOND_RUN ? (
                    <MedicationSchedule />
                  ) : (
                    <MedicationEntryTabs />
                  )}
                </section>
              )}
              
              {/* Horizontal divider for section separation */}
              <div className="my-16 border-t border-gray-200" aria-hidden="true"></div>          
            
              {/* App screenshots with proper alt text and aria-labels */}
              <section 
                className="my-16" 
                aria-label="Application screenshots"
                itemScope 
                itemType="https://schema.org/ImageGallery"
                data-test-id="app-screenshots"
              >
                <motion.div 
                  className="relative"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  <Suspense fallback={
                    <div className="aspect-[16/9] w-full bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl animate-pulse">
                      <div className="h-full w-full flex items-center justify-center">
                        <div className="w-10 h-10 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    </div>
                  }>
                    <ErrorBoundary>
                      {/* Always use HeroFull for both mobile and desktop */}
                      <div 
                        aria-label="Application preview"
                        role="img"
                        className="mx-auto"
                        data-view-mode={userExperience}
                        data-is-mobile={isMobile.toString()}
                        data-performance-tracking="enabled"
                      >
                        <HeroFull />
                      </div>
                    </ErrorBoundary>
                  </Suspense>
                </motion.div>
                <meta itemProp="name" content="Meduler App Screenshots" />
                <meta itemProp="description" content="Medication management features including analytics, calendar, and critical alerts" />
              </section>

              {/* FAQ Section - Only shown for first-time users */}
              {userExperience === USER_EXPERIENCE_STATES.FIRST_RUN && (
                <section 
                  className="py-8 my-12"
                  aria-labelledby="faq-heading"
                  data-test-id="faq-section-first-run"
                >
                  <h2 id="faq-heading" className="text-3xl font-bold text-center text-gray-900 mb-10">
                    Frequently Asked Questions
                  </h2>
                  <Suspense fallback={
                    <div className="max-w-3xl mx-auto p-8 border border-purple-100 rounded-xl bg-white/50 animate-pulse">
                      <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
                      <div className="h-24 bg-gray-100 rounded mb-4"></div>
                      <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  }>
                    <ErrorBoundary>
                      <FAQSection />
                    </ErrorBoundary>
                  </Suspense>
                </section>
              )}
            </div>
          </main>
          
          {/* Add Medication Dialog */}
          <Dialog open={isAddMedicationOpen} onOpenChange={setIsAddMedicationOpen}>
            <DialogContent 
              className="p-0 overflow-hidden border-none rounded-lg bg-white dark:bg-gray-950 shadow-xl max-h-[90vh] max-w-4xl data-[state=open]:translate-y-0 data-[state=open]:!top-2 data-[state=open]:slide-in-from-top-2"
            >
              {/* ACCESSIBILITY FIX: DialogTitle MUST be first direct child of DialogContent */}
              <DialogTitle className="sr-only">Add Medication</DialogTitle>
              
              {/* Embed the dialog inside our wrapper with proper accessibility structure */}
              <div className="w-full h-full rounded-lg overflow-hidden">
                <AddMedicationDialog
                  open={isAddMedicationOpen}
                  onOpenChange={setIsAddMedicationOpen}
                  insideDialogContent={true}
                  onAddMedication={() => {
                    // Medication added — state updates handled by event bus
                  }}
                />
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
